import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Grid, Alert, MenuItem, Chip, IconButton
} from '@mui/material';
import { Add, Person, Edit, Delete, Lock } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import api from '../services/api';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: '',
    department: ''
  });
  const [editUser, setEditUser] = useState(null);
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(null);

  const user = useSelector(state => state.auth.user);

  const roleOptions = user?.role === 'SuperAdmin' 
    ? ['Admin'] 
    : ['Sample Collector', 'HOD', 'Tester'];

  const departmentOptions = ['Chemical', 'Instrument', 'Micro', 'GM'];

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data.users);
    } catch (error) {
      setError('Failed to fetch users');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (editUser) {
        await api.put(`/users/${editUser._id}`, formData);
        setSuccess('User updated successfully!');
      } else {
        await api.post('/users', formData);
        setSuccess('User created successfully!');
      }
      setOpen(false);
      setEditUser(null);
      setFormData({ name: '', email: '', password: '', role: '', department: '' });
      fetchUsers();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to save user');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleEdit = (userData) => {
    setEditUser(userData);
    setFormData({
      name: userData.name,
      email: userData.email,
      password: '',
      role: userData.role,
      department: userData.department || ''
    });
    setOpen(true);
  };

  const handleDelete = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await api.delete(`/users/${userId}`);
        setSuccess('User deleted successfully!');
        fetchUsers();
      } catch (error) {
        setError(error.response?.data?.message || 'Failed to delete user');
      }
    }
  };

  const handlePasswordChange = async () => {
    if (!newPassword || newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    try {
      await api.put(`/users/${selectedUserId}/password`, { password: newPassword });
      setSuccess('Password changed successfully!');
      setPasswordDialog(false);
      setNewPassword('');
      setSelectedUserId(null);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to change password');
    }
  };

  const openPasswordDialog = (userId) => {
    setSelectedUserId(userId);
    setPasswordDialog(true);
  };

  const getRoleColor = (role) => {
    const colors = {
      'SuperAdmin': 'error',
      'Admin': 'primary',
      'Sample Collector': 'info',
      'HOD': 'secondary',
      'Tester': 'success'
    };
    return colors[role] || 'default';
  };

  if (!['SuperAdmin', 'Admin'].includes(user?.role)) {
    return (
      <Box>
        <Alert severity="error">Access denied. Only SuperAdmin and Admin can manage users.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">User Management</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            setEditUser(null);
            setFormData({ name: '', email: '', password: '', role: '', department: '' });
            setOpen(true);
          }}
        >
          Create User
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Department</TableCell>
              <TableCell>Company</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((userData) => (
              <TableRow key={userData._id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Person sx={{ mr: 1 }} />
                    {userData.name}
                  </Box>
                </TableCell>
                <TableCell>{userData.email}</TableCell>
                <TableCell>
                  <Chip 
                    label={userData.role} 
                    color={getRoleColor(userData.role)}
                    size="small"
                  />
                </TableCell>
                <TableCell>{userData.department || '-'}</TableCell>
                <TableCell>{userData.company?.name || '-'}</TableCell>
                <TableCell>
                  <Chip 
                    label={userData.active ? 'Active' : 'Inactive'}
                    color={userData.active ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <IconButton 
                    size="small" 
                    onClick={() => handleEdit(userData)}
                    color="primary"
                  >
                    <Edit />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    onClick={() => openPasswordDialog(userData._id)}
                    color="secondary"
                  >
                    <Lock />
                  </IconButton>
                  {userData.role !== 'Admin' && userData.role !== 'Sample Collector' && (
                    <IconButton 
                      size="small" 
                      onClick={() => handleDelete(userData._id)}
                      color="error"
                    >
                      <Delete />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>{editUser ? 'Edit User' : 'Create New User'}</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  name="name"
                  label="Full Name"
                  value={formData.name}
                  onChange={handleChange}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="email"
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  fullWidth
                  required
                />
              </Grid>
              {!editUser && (
                <Grid item xs={12}>
                  <TextField
                    name="password"
                    label="Password"
                    type="password"
                    value={formData.password}
                    onChange={handleChange}
                    fullWidth
                    required
                    helperText="Minimum 6 characters"
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  name="role"
                  label="Role"
                  select
                  value={formData.role}
                  onChange={handleChange}
                  fullWidth
                  required
                >
                  {roleOptions.map((role) => (
                    <MenuItem key={role} value={role}>
                      {role}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              {formData.role !== 'Admin' && formData.role !== 'Sample Collector' && (
                <Grid item xs={12}>
                  <TextField
                    name="department"
                    label="Department"
                    select
                    value={formData.department}
                    onChange={handleChange}
                    fullWidth
                    required
                  >
                    {departmentOptions.map((dept) => (
                      <MenuItem key={dept} value={dept}>
                        {dept}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? (editUser ? 'Updating...' : 'Creating...') : (editUser ? 'Update User' : 'Create User')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={passwordDialog} onClose={() => setPasswordDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <TextField
            label="New Password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            fullWidth
            margin="normal"
            helperText="Minimum 6 characters"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialog(false)}>Cancel</Button>
          <Button onClick={handlePasswordChange} variant="contained">
            Change Password
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;